import { toast } from "react-toastify";
import { DiscountType } from "../Types/PromoCodeTypes";
import {
  apiPublicPost,
  apiPublicRawPost,
  apiPublicGet,
  apiPost,
  apiGet,
  apiPut,
  apiDelete
} from "./apiClient";

// ========================
// Typdefinitioner
// ========================
export interface PromoCode {
  id: number;
  code: string;
  description?: string;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountAmount?: number;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  usageLimit?: number;
  usageCount: number;
  perUserLimit?: number;
}

export interface PromoCodeVersion {
  id: number;
  promoCodeId: number;
  versionNumber: number;
  changeType: string;
  code: string;
  description?: string;
  discountType: DiscountType;
  discountValue: number;
  maxDiscountAmount?: number | null;
  startDate: string;
  endDate?: string | null;
  isActive: boolean;
  usageLimit?: number | null;
  perUserLimit?: number | null;
  createdAtUtc: string;
}

export interface PromoCodeVerification extends PromoCode {}

export interface UsePromoDto {
  code: string;
  userId?: string;
}

// ========================
// Service
// ========================
export const PromoCodeService = {
  /* -------------------------------------------------------
     PUBLIC — VERIFY PROMOCODE
  ------------------------------------------------------- */
  verify: async (
    promoCode: string
  ): Promise<{ message: string; promoCode?: PromoCodeVerification }> => {
    try {
      const response = await apiPublicRawPost(`/PromoCode/verifypromo`, promoCode);
      const res = await response.json().catch(() => ({}));
      toast.success("Kampanjkod verifierad.");
      return res;
    } catch (err) {
      console.error("Error verifying promo code", err);
      toast.error("Kunde inte verifiera kampanjkoden.");
      return { message: "Kunde inte verifiera kampanjkoden." };
    }
  },

  /* -------------------------------------------------------
     PUBLIC — USE PROMOCODE
  ------------------------------------------------------- */
  use: async (dto: UsePromoDto): Promise<{ message: string }> => {
    try {
      return await apiPublicPost(`/PromoCode/use`, dto);
    } catch (err) {
      console.error("Error using promo code", err);
      return { message: "Error applying promo code" };
    }
  },

  /* -------------------------------------------------------
     ADMIN — CREATE (PROTECTED)
  ------------------------------------------------------- */
  create: async (dto: PromoCode): Promise<{ message: string }> => {
    try {
      const res = await apiPost(`/PromoCode/create`, dto);
      toast.success("Kampanjkod skapad.");
      return res;
    } catch (err) {
      console.error("Error creating promo code", err);
      toast.error("Kunde inte skapa kampanjkod.");
      return { message: "Error creating promo code" };
    }
  },

  /* -------------------------------------------------------
     ADMIN — READ ALL (PROTECTED)
  ------------------------------------------------------- */
  getAll: async (): Promise<PromoCode[]> => {
    try {
      return await apiGet<PromoCode[]>(`/PromoCode/all`);
    } catch (err) {
      console.error("Error fetching promo codes", err);
      toast.error("Kunde inte hämta kampanjkoder.");
      return [];
    }
  },

  /* -------------------------------------------------------
     ADMIN — READ BY ID (PROTECTED)
  ------------------------------------------------------- */
  getById: async (id: number): Promise<PromoCode | null> => {
    try {
      return await apiGet<PromoCode>(`/PromoCode/${id}`);
    } catch (err: any) {
      if (err?.status === 404) return null;
      console.error("Error fetching promo code by ID", err);
      return null;
    }
  },

  getVersions: async (id: number): Promise<PromoCodeVersion[]> => {
    try {
      return await apiGet<PromoCodeVersion[]>(`/PromoCode/${id}/versions`);
    } catch (err) {
      console.error("Error fetching promo code versions", err);
      return [];
    }
  },

  /* -------------------------------------------------------
     ADMIN — UPDATE (PROTECTED)
  ------------------------------------------------------- */
  update: async (id: number, promo: PromoCode): Promise<{ message: string }> => {
    try {
      const res = await apiPut(`/PromoCode/${id}`, promo);
      toast.success("Kampanjkod uppdaterad.");
      return res;
    } catch (err) {
      console.error("Error updating promo code", err);
      toast.error("Kunde inte uppdatera kampanjkod.");
      return { message: "Error updating promo code" };
    }
  },

  /* -------------------------------------------------------
     ADMIN — DELETE (PROTECTED)
  ------------------------------------------------------- */
  delete: async (id: number): Promise<{ message: string }> => {
    try {
      const res = await apiDelete(`/PromoCode/${id}`);
      toast.success("Kampanjkod borttagen.");
      return res;
    } catch (err) {
      console.error("Error deleting promo code", err);
      toast.error("Kunde inte ta bort kampanjkod.");
      return { message: "Error deleting promo code" };
    }
  }
};
